import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Link } from "wouter";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import QuizApi from '../QuizApi';

import Spinner from "react-bootstrap/Spinner";
import Container from "react-bootstrap/Container";
import Button from "react-bootstrap/Button";
import BTable from 'react-bootstrap/Table';

import { useTable, useSortBy } from 'react-table';
import { absURL } from '../utils';
import AdminHeader from './AdminHeader';

const TYPE_LABELS = {
  Quiz: "Quiz",
  Poll: "Undersökning",
  Free: "Fritext",
  Musicquiz: "Music quiz",
};

const MONTH_NAMES = [
  'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
  'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December',
];

const GROUP_BUTTONS = [
  { label: 'Ingen gruppering', value: null },
  { label: 'Grupp', value: 'group' },
  { label: 'Typ', value: 'type' },
  { label: 'Skapare', value: 'creator' },
  { label: 'År och månad', value: 'yearMonth' },
];

function QuizTable({ columns, data }) {
  const { getTableProps, headerGroups, rows, prepareRow } = useTable(
    { columns, data },
    useSortBy
  );

  return (
    <div className="adminTableScroll adminResponsiveTable">
      <BTable striped hover size="md" {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => {
                const sortProps = column.canSort ? column.getSortByToggleProps() : {};
                return (
                  <th
                    {...column.getHeaderProps(sortProps)}
                    style={{ cursor: column.canSort ? 'pointer' : 'default' }}>
                    {column.render('Header')}
                    {column.canSort && (
                      <span>{column.isSorted ? (column.isSortedDesc ? ' ↓' : ' ↑') : ''}</span>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.map(row => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => (
                  <td
                    {...cell.getCellProps()}
                    data-label={typeof cell.column.Header === 'string' ? cell.column.Header : ''}>
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </BTable>
    </div>
  );
}

export default function AdminQuizList() {

  const groupParam = new URLSearchParams(window.location.search).get('group');

  const [shouldFetchList, setShouldFetchList] = useState(true);
  const [loading, setLoading] = useState(true);
  const [quizGroups, setQuizGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupBy, setGroupBy] = useState(groupParam ? 'group' : null);
  const [newGroupName, setNewGroupName] = useState('');
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  const quizInstanceCreate = useCallback(quizId => {
    const fn = async () => {
      setLoading(true);
      const result = await QuizApi.quizInstanceCreate(quizId);
      setLoading(false);
      if (result && result.success && result.response.defaultAccessId) {
        window.open(absURL(`/${result.response.defaultAccessId}/admin`), '_blank');
      }
    };
    fn();
  }, []);

  const createGroup = useCallback(async () => {
    if (!newGroupName.trim()) return;
    setSavingGroup(true);
    await QuizApi.quizGroupCreate(newGroupName.trim());
    setNewGroupName('');
    setShowNewGroup(false);
    setSavingGroup(false);
    setShouldFetchList(true);
  }, [newGroupName]);

  const quizClone = useCallback(quizId => {
    const fn = async () => {
      setLoading(true);
      await QuizApi.quizClone(quizId);
      setShouldFetchList(true);
    };
    fn();
  }, []);

  useEffect(() => {
    if (shouldFetchList) {
      const fetch = async () => {
        setLoading(true);
        const result = await QuizApi.userQuizListGet();
        setQuizGroups(result?.response?.quizGroups ?? []);
        setLoading(false);
        setShouldFetchList(false);
      };
      fetch();
    }
  }, [shouldFetchList]);

  const scopedGroups = useMemo(() => {
    if (!groupParam) return quizGroups;
    return quizGroups.filter(g => groupParam === 'none' ? g.id === null : g.id === groupParam);
  }, [quizGroups, groupParam]);

  const allQuizzes = useMemo(() => scopedGroups.flatMap(g => g.quizzes), [scopedGroups]);

  const innerColumns = useMemo(() => [
    {
      Header: 'Namn',
      accessor: 'name',
    },
    {
      Header: 'Typ',
      id: 'type',
      accessor: row => TYPE_LABELS[row.type] || '',
    },
    {
      Header: 'Skapad',
      id: 'createdAt',
      accessor: row => new Date(row.createdAt).toISOString().slice(0, 10),
    },
    {
      Header: 'Skapare',
      accessor: 'createdByName',
      Cell: row => row.value || '',
    },
    {
      Header: 'Tid (min)',
      id: 'duration',
      accessor: row => Number(row.durationSeconds / 60),
    },
    {
      Header: 'Antal',
      id: 'questionCount',
      disableSortBy: true,
      accessor: row => {
        if (row.questionCount === 0 && typeof row.quizGenState !== 'undefined') {
          switch (row.quizGenState) {
            case 0:
            case 1: return <Spinner animation="border" size="sm" />;
            case 2: return row.questionCount;
            case 3: return <div className="text-quizError"><FontAwesomeIcon icon={faExclamationCircle} /></div>;
            default: break;
          }
        }
        return row.questionCount || 0;
      },
    },
    {
      accessor: 'id',
      disableSortBy: true,
      Cell: row => (
        <div>
          <Button onClick={() => quizInstanceCreate(row.value)} className="btn-quizPrimaryDark btn-sm mr-1">Starta</Button>
          <Link href={absURL(`/admin/quiz/${row.value}/edit`)}>
            <Button variant="outline-secondary" className="btn-sm mr-1">Info</Button>
          </Link>
          <Button onClick={() => quizClone(row.value)} variant="outline-secondary" className="btn-sm mr-1">Kopiera</Button>
          <Link href={absURL(`/admin/quiz/${row.value}/instances`)}>
            <Button variant="outline-primary" className="btn-sm mr-1">Historik</Button>
          </Link>
        </div>
      ),
    },
  ], [quizInstanceCreate, quizClone]);

  const flatColumns = useMemo(() => [{
    Header: 'Lista över quizzar och undersökningar',
    columns: innerColumns,
  }], [innerColumns]);

  const filterQuizzes = (quizzes, term) => {
    if (!term) return quizzes;
    const lower = term.toLowerCase();
    return quizzes.filter(q =>
      (q.name || '').toLowerCase().includes(lower) ||
      (q.createdByName || '').toLowerCase().includes(lower) ||
      (TYPE_LABELS[q.type] || '').toLowerCase().includes(lower) ||
      new Date(q.createdAt).toISOString().slice(0, 10).includes(lower)
    );
  };

  const filteredList = useMemo(
    () => filterQuizzes(allQuizzes, searchTerm),
    [allQuizzes, searchTerm]
  );

  const groupedData = useMemo(() => {
    if (!groupBy) return null;

    if (groupBy === 'group') {
      return scopedGroups
        .map(g => [g.id === null ? 'Utan grupp' : g.name, filterQuizzes(g.quizzes, searchTerm)])
        .filter(([, items]) => items.length > 0);
    }

    const groups = {};
    filteredList.forEach(q => {
      let key;
      if (groupBy === 'type') {
        key = TYPE_LABELS[q.type] || 'Okänd';
      } else if (groupBy === 'creator') {
        key = q.createdByName || 'Okänd';
      } else {
        const d = new Date(q.createdAt);
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(q);
    });
    return Object.entries(groups).sort(([a], [b]) =>
      groupBy === 'yearMonth' ? b.localeCompare(a) : a.localeCompare(b, 'sv')
    );
  }, [filteredList, groupBy, scopedGroups, searchTerm]);

  const formatGroupLabel = key => {
    if (groupBy !== 'yearMonth') return key;
    const [year, month] = key.split('-');
    return `${MONTH_NAMES[parseInt(month, 10) - 1]} ${year}`;
  };

  return (
    <div id="userMainPage">
      <div>
        {loading && (
          <Container>
            <div className="d-flex justify-content-center align-items-center h-100">
              <Spinner animation="border" />
            </div>
          </Container>
        )}

        {!loading && (
          <div>
            <AdminHeader />
            <div className="adminContentWrapper">

              <div className="d-flex align-items-center flex-wrap mt-2 ml-2" style={{ gap: '8px' }}>
                <Link href={absURL(`/admin`)}>
                  <Button variant="outline-secondary" className="btn-sm">Tillbaka</Button>
                </Link>

                <div className="d-flex flex-wrap align-items-center ml-auto" style={{ gap: '8px' }}>
                  <Link href={absURL(`/admin/createQuiz`)}>
                    <Button className="btn-quizPrimaryDark">Skapa quiz</Button>
                  </Link>
                  {!showNewGroup ? (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowNewGroup(true)}>
                      Ny grupp
                    </Button>
                  ) : (
                    <div className="d-flex flex-wrap align-items-center" style={{ gap: '6px' }}>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ maxWidth: '180px' }}
                        placeholder="Gruppnamn..."
                        value={newGroupName}
                        autoFocus
                        onChange={e => setNewGroupName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') createGroup(); if (e.key === 'Escape') setShowNewGroup(false); }}
                      />
                      <Button
                        className="btn-sm btn-quizPrimaryDark"
                        disabled={savingGroup || !newGroupName.trim()}
                        onClick={createGroup}>
                        Spara
                      </Button>
                      <Button
                        variant="outline-secondary"
                        className="btn-sm"
                        onClick={() => { setShowNewGroup(false); setNewGroupName(''); }}>
                        Avbryt
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {allQuizzes.length > 0 && (
                <div id="userQuizList" className="mt-3">

                  <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: '10px' }}>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ maxWidth: '220px' }}
                      placeholder="Sök i listan..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                    {/* Grupperingsknapparna gjorde raden stökig - dolda tills vidare, se GROUP_BUTTONS ovan.
                    <div className="d-flex flex-wrap" role="group" aria-label="Gruppering" style={{ gap: '4px' }}>
                      {GROUP_BUTTONS.map(btn => (
                        <button
                          key={String(btn.value)}
                          type="button"
                          className={`btn btn-sm ${groupBy === btn.value ? 'btn-quizPrimaryDark' : 'btn-outline-secondary'}`}
                          onClick={() => setGroupBy(btn.value)}>
                          {btn.label}
                        </button>
                      ))}
                    </div>
                    */}
                  </div>

                  {!groupBy ? (
                    filteredList.length > 0
                      ? <QuizTable columns={flatColumns} data={filteredList} />
                      : <p className="text-muted">Inga quizzar matchar sökningen.</p>
                  ) : (
                    groupedData && groupedData.length > 0
                      ? groupedData.map(([key, items]) => (
                          <div key={key} className="mb-4">
                            <h5 className="border-bottom pb-1 mb-2">
                              {formatGroupLabel(key)}
                              <span className="text-muted ml-2" style={{ fontSize: '0.85em' }}>({items.length} st)</span>
                            </h5>
                            <QuizTable columns={innerColumns} data={items} />
                          </div>
                        ))
                      : <p className="text-muted">Inga quizzar matchar sökningen.</p>
                  )}

                </div>
              )}

            </div>
          </div>
        )}
      </div>
    </div>
  );
}
